import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Typography, Grid, Box, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip,
    CircularProgress, Alert, IconButton, Tooltip, TextField, InputAdornment,
    FormControl, InputLabel, Select, Avatar, TablePagination
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import SettingsIcon from '@mui/icons-material/Settings';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import EmailIcon from '@mui/icons-material/Email';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';
import { useNotify } from 'react-admin';

const host = config.production.host;

const getJwt = () => localStorage.getItem("feathers-jwt");

const ROLE_COLORS = {
    admin: '#e85a2c',
    user: '#2196f3',
    guest: '#9e9e9e',
    moderator: '#ff9800',
    editor: '#4caf50',
    collaboratore: '#00bcd4'
};

const ROLE_LABELS = {
    admin: 'admin',
    gruppi: 'gruppi'
};

function UsersViewPage() {
    const notify = useNotify();
    const navigate = useNavigate();
    const [users, setUsers] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [filterRole, setFilterRole] = React.useState('all');
    const [filterStatus, setFilterStatus] = React.useState('all');
    const [usersGruppi, setUsersGruppi] = React.useState({});

    // Paginazione
    const [page, setPage] = React.useState(0);
    const [limit, setLimit] = React.useState(25);
    const [total, setTotal] = React.useState(0);

    const getInitials = (nome, cognome) => {
        const n = nome ? nome.charAt(0).toUpperCase() : '';
        const c = cognome ? cognome.charAt(0).toUpperCase() : '';
        return `${n}${c}` || '?';
    };

    const handleViewUser = (user) => {
        navigate(`/users/${user.id}`);
    };

    const handleEditUser = (user) => {
        navigate(`/users/${user.id}/edit`);
    };

    // Configurazione colonne
    const COLUMN_CONFIG = {
        avatar: {
            label: 'Avatar',
            key: 'avatar',
            align: 'center',
            width: 80,
            render: (value, row) => (
                <Avatar
                    sx={{
                        bgcolor: ROLE_COLORS[row.roles] || '#757575',
                        width: 40,
                        height: 40,
                        margin: 'auto',
                        fontSize: '0.875rem',
                        fontWeight: 'bold'
                    }}
                >
                    {getInitials(row.nome, row.cognome)}
                </Avatar>
            )
        },
        nomeCompleto: {
            label: 'Nome Completo',
            key: 'nomeCompleto',
            align: 'left',
            width: 200,
            render: (value, row) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.nome && row.cognome ? `${row.nome} ${row.cognome}` : 'N/A'}
                    </Typography>
                </Box>
            )
        },
        email: {
            label: 'Email',
            key: 'email',
            align: 'left',
            width: 200,
            render: (value) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ color: '#999', fontSize: 16 }} />
                    <Typography variant="body2">{value || '-'}</Typography>
                </Box>
            )
        },
        roles: {
            label: 'Ruolo',
            key: 'roles',
            align: 'center',
            width: 150,
            render: (value) => (
                <Chip
                    icon={<PersonIcon />}
                    label={ROLE_LABELS[value] || value}
                    size="small"
                    sx={{
                        backgroundColor: ROLE_COLORS[value] || '#757575',
                        color: '#fff',
                        fontWeight: 'bold',
                        '& .MuiChip-icon': { color: '#fff' }
                    }}
                />
            )
        },
        gruppi: {
            label: 'Gruppi',
            key: 'gruppi',
            align: 'left',
            width: 200,
            render: (value, row) => {
                // Se è admin, non serve mostrare gruppi
                if (row.roles === 'admin') {
                    return <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Admin completo</Typography>;
                }

                const gruppi = usersGruppi[row.id] || [];
                if (gruppi.length === 0) {
                    return <Typography variant="caption" color="warning.main">⚠️ Nessun gruppo</Typography>;
                }
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {gruppi.map((gruppo, idx) => (
                            <Chip
                                key={idx}
                                icon={<GroupIcon />}
                                label={gruppo.nomeGruppo}
                                size="small"
                                variant="outlined"
                                sx={{
                                    fontSize: '0.75rem',
                                    borderColor: '#2196f3',
                                    color: '#2196f3'
                                }}
                            />
                        ))}
                    </Box>
                );
            }
        },
        status: {
            label: 'Stato',
            key: 'status',
            align: 'center',
            width: 120,
            render: (value) => {
                const isActive = value === 1 || value === true;
                return (
                    <Chip
                        icon={isActive ? <CheckCircleIcon /> : <CancelIcon />}
                        label={isActive ? 'Attivo' : 'Disattivo'}
                        size="small"
                        color={isActive ? 'success' : 'default'}
                        variant={isActive ? 'filled' : 'outlined'}
                    />
                );
            }
        },
        actions: {
            label: 'Azioni',
            key: 'actions',
            align: 'center',
            width: 120,
            render: (value, row) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Visualizza dettagli">
                        <IconButton size="small" onClick={() => handleViewUser(row)}>
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifica">
                        <IconButton size="small" onClick={() => handleEditUser(row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    };

    // Costruisce query FeathersJS
    const buildQuery = () => {
        const query = {
            $limit: limit,
            $skip: page * limit
        };

        const filters = {};

        // Ricerca full-text
        if (searchTerm.trim()) {
            const search = `%${searchTerm.trim()}%`;
            filters.$or = [
                { email: { $like: search } },
                { nome: { $like: search } },
                { cognome: { $like: search } }
            ];
        }

        // Filtro ruolo
        if (filterRole !== 'all') {
            filters.roles = filterRole;
        }

        // Filtro status
        if (filterStatus !== 'all') {
            filters.status = filterStatus === 'active' ? 1 : 0;
        }

        if (Object.keys(filters).length > 0) {
            Object.assign(query, filters);
        }

        return query;
    };

    // Carica gruppi di un utente
    const loadUserGruppi = async (userId) => {
        try {
            const jwt = getJwt();
            const response = await axios.get(`${host}/proc-get-users-gruppi`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: { userId }
            });
            return response.data || [];
        } catch (error) {
            console.error(`Errore nel recupero gruppi per user ${userId}:`, error);
            return [];
        }
    };

    // Carica utenti e i loro gruppi
    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();
            const query = buildQuery();

            const response = await axios.get(`${host}/users`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: query
            });

            const usersData = response.data.data || [];
            setUsers(usersData);
            setTotal(response.data.total || 0);

            // Carica i gruppi solo per utenti NON admin
            const gruppiMap = {};
            for (const user of usersData) {
                if (user.roles !== 'admin') {
                    const gruppi = await loadUserGruppi(user.id);
                    gruppiMap[user.id] = gruppi;
                } else {
                    gruppiMap[user.id] = []; // Admin non ha gruppi
                }
            }
            setUsersGruppi(gruppiMap);
        } catch (error) {
            console.error("Errore nel recupero utenti:", error);
            setError("Errore nel caricamento degli utenti");
            notify("Errore nel caricamento degli utenti", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleLimitChange = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const resetFilters = () => {
        setSearchTerm('');
        setFilterRole('all');
        setFilterStatus('all');
        setPage(0);
    };

    React.useEffect(() => {
        loadUsers();
    }, [page, limit, searchTerm, filterRole, filterStatus]);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        👥 Vista Avanzata Utenti
                    </Typography>
                    <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AddCircleIcon />}
                            sx={{
                                color: '#4A90E2',
                                borderColor: '#4A90E2',
                                fontWeight: 600,
                                borderRadius: 2,
                                py: 1.5,
                                '&:hover': {
                                    borderColor: '#357ABD',
                                    color: '#357ABD',
                                    bgcolor: 'rgba(74, 144, 226, 0.04)'
                                }
                            }}
                            onClick={() => navigate('/users/create')}>
                            Nuovo Utente
                        </Button>
                    </Card>
                </Box>

                {/* Filtri */}
                <Card sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                    <Grid container spacing={2} alignItems="center">

                        {/* Ricerca */}
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Cerca nome, cognome, email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        {/* Filtro Ruolo */}
                        <Grid item xs={6} md={2.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Ruolo</InputLabel>
                                <Select
                                    value={filterRole}
                                    label="Ruolo"
                                    onChange={(e) => {
                                        setFilterRole(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="all">Tutti</MenuItem>
                                    {Object.keys(ROLE_LABELS).map(role => (
                                        <MenuItem key={role} value={role}>
                                            {ROLE_LABELS[role]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Filtro Status */}
                        <Grid item xs={6} md={2.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Stato</InputLabel>
                                <Select
                                    value={filterStatus}
                                    label="Stato"
                                    onChange={(e) => {
                                        setFilterStatus(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="all">Tutti</MenuItem>
                                    <MenuItem value="active">Attivi</MenuItem>
                                    <MenuItem value="inactive">Disattivi</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Controlli */}
                        <Grid item xs={12} md={3}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Tooltip title="Cancella filtri">
                                    <IconButton
                                        size="small"
                                        onClick={resetFilters}
                                        disabled={searchTerm === '' && filterRole === 'all' && filterStatus === 'all'}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Aggiorna dati">
                                    <IconButton size="small" onClick={loadUsers} disabled={loading}>
                                        <RefreshIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>

                {/* Tabella */}
                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                    {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

                    {loading && !users.length ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : users.length > 0 ? (
                        <>
                            <TableContainer sx={{ maxHeight: 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['avatar', 'nomeCompleto', 'email', 'roles', 'gruppi', 'status', 'actions'].map(columnKey => {
                                                const config = COLUMN_CONFIG[columnKey];
                                                return (
                                                    <TableCell
                                                        key={columnKey}
                                                        align={config.align}
                                                        sx={{
                                                            fontWeight: 600,
                                                            width: config.width,
                                                            bgcolor: '#f5f5f5',
                                                            color: '#1976d2'
                                                        }}
                                                    >
                                                        {config.label}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map((user, index) => (
                                            <TableRow
                                                key={user.id}
                                                sx={{
                                                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                                                    bgcolor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'inherit'
                                                }}
                                            >
                                                {['avatar', 'nomeCompleto', 'email', 'roles', 'gruppi', 'status', 'actions'].map(columnKey => {
                                                    const config = COLUMN_CONFIG[columnKey];
                                                    const value = user[config.key];
                                                    return (
                                                        <TableCell key={columnKey} align={config.align} sx={{ py: 1 }}>
                                                            {config.render ? config.render(value, user) : value || '-'}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={handlePageChange}
                                rowsPerPage={limit}
                                onRowsPerPageChange={handleLimitChange}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                labelRowsPerPage="Righe per pagina:"
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                                }
                                sx={{ borderTop: '1px solid #e0e0e0' }}
                            />
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 6 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {searchTerm || filterRole !== 'all' || filterStatus !== 'all' ?
                                    'Nessun utente trovato con i filtri applicati' :
                                    'Nessun utente presente'
                                }
                            </Typography>
                            {(searchTerm || filterRole !== 'all' || filterStatus !== 'all') && (
                                <Button variant="outlined" onClick={resetFilters} sx={{ mt: 2 }}>
                                    Cancella filtri
                                </Button>
                            )}
                        </Box>
                    )}
                </Card>

            </motion.div>
        </Box>
    );
}

export default UsersViewPage;